import api from '@/services/api/client';
import { getApiMessage } from '@/lib/apiMessage';

export interface ParserSupplier {
  id?: string | number;
  SupplierId?: string | number;
  supplierId?: string | number;
  SupplierName?: string;
  supplierName?: string;
  name?: string;
  shortName?: string;
  SupplierShortName?: string;
  logo?: string;
  logoUrl?: string;
  [key: string]: any;
}

// GET /v1/Supplier/GetParserSuppliers?supplierName=...
export async function getParserSuppliers(search?: string): Promise<ParserSupplier[]> {
  const resp = await api.get('/v1/Supplier/GetParserSuppliers', {
    params: search && search.trim().length > 0 ? { supplierName: search } : undefined,
  });
  const data = resp?.data;
  const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
  return list as ParserSupplier[];
}

export interface ParserSandboxResponse {
  success?: boolean;
  message?: string;
  data?: {
    results?: Record<string, any>;
    lines?: Array<Record<string, any>>;
    confidence?: Record<string, number>;
    [key: string]: any;
  };
  [key: string]: any;
}

// POST /v1/ParserBase/DataExtractionSandbox multipart/form-data
export async function parseSandboxData(file: File, supplierName: string): Promise<ParserSandboxResponse> {
  const form = new FormData();
  form.append('file', file);
  form.append('SupplierName', supplierName);

  try {
    const resp = await api.post('/v1/ParserBase/DataExtractionSandbox', form);
    const data = resp?.data;
    const normalized = (data?.data ?? data) as ParserSandboxResponse;
    // Ensure message normalization is available to callers
    (normalized as any).__message = getApiMessage(data) || normalized?.message || normalized?.Message;
    return normalized;
  } catch (err: any) {
    const msg = getApiMessage(err) || err?.message || 'Parser sandbox request failed';
    throw new Error(msg);
  }
}

// POST /v1/supplier/parser-portal-request multipart/form-data
export interface ParserPortalRequestPayload {
  supplierName: string; // Portal Name
  email: string; // Contact Email
  sampleBill: File; // PDF file
  additionalInfo?: string; // Optional details
  authUserId?: string | number | null; // Optional auth user id from session
}

export interface ParserPortalRequestResponse {
  success?: boolean;
  message?: string;
  [key: string]: any;
}

export async function submitParserPortalRequest(
  payload: ParserPortalRequestPayload
): Promise<ParserPortalRequestResponse> {
  const form = new FormData();
  form.append('supplierName', payload.supplierName);
  form.append('email', payload.email);
  form.append('sampleBill', payload.sampleBill);
  if (payload.additionalInfo) form.append('additionalInfo', payload.additionalInfo);
  if (payload.authUserId != null) {
    // Backend expects lowercase 'authuserId' in some endpoints; include that here
    form.append('authuserId', String(payload.authUserId));
  }

  const resp = await api.post('/v1/supplier/parser-portal-request', form);
  const data = resp?.data;
  return (data?.data ?? data) as ParserPortalRequestResponse;
}

// POST /v1/parserdata/ExportSandboxDataToCsv - returns CSV as blob
export async function exportSandboxDataToCsv(body: any): Promise<Blob> {
  const resp = await api.post('/v1/parserdata/ExportSandboxDataToCsv', body, {
    responseType: 'blob',
  });
  return resp.data as Blob;
}
